// Общее для форм в модалках: классы полей, ошибка, футер «удалить · отмена · сохранить».
import { useState, type ReactNode, type KeyboardEvent } from 'react';
import { Button } from './Button';

export const fieldInput = 'border border-line rounded-md px-3 py-2 text-base w-full bg-paper';
/** Нативный select, поиск, кнопка даты в тулбаре — одна высота и рамка с остальными контролами. */
export const control = 'h-8 min-h-0 border border-gray-300 rounded px-2 text-sm bg-white';
export const fieldLabel = 'flex flex-col gap-1 font-medium';
/** Save упёрся в чужую правку (PT412): поля уже обновлены, человек смотрит и жмёт ещё раз. */
export const SAVE_CONFLICT = 'Someone changed this task just now — check the fields and save again.';

export function FormError({ error }: { error: string | null }) {
  return error ? <p role="alert" className="text-danger font-medium">{error}</p> : null;
}

/** Слот left у ModalFooter: кнопка удаления, по нажатию на её месте вопрос с «Yes, delete / No»; children — что стоит рядом всегда. */
export function DeleteConfirm({ label, question, busy, disabled, onDelete, children }: { label: string; question: ReactNode; busy: boolean; disabled?: boolean; onDelete: () => void; children?: ReactNode }) {
  const [asking, setAsking] = useState(false);
  return (
    <>
      {asking ? (
        <>
          <span className="font-medium">{question}</span>
          <Button variant="danger" onClick={onDelete} disabled={busy}>Yes, delete</Button>
          <Button variant="ghost" onClick={() => setAsking(false)}>No</Button>
        </>
      ) : <Button variant="danger-text" onClick={() => setAsking(true)} disabled={busy || disabled}>{label}</Button>}
      {children}
    </>
  );
}

/** Ctrl/Cmd+Enter в форме карточки — то же, что Save. Вешать на <form>. */
export const submitOnCtrlEnter = (e: KeyboardEvent<HTMLFormElement>) => {
  if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) { e.preventDefault(); e.currentTarget.requestSubmit(); }
};

/** Кнопки в слоте footer у Modal: left — что слева (обычно удаление); Save — submit формы с id form из тела. */
export function ModalFooter({ left, busy, onCancel, saveDisabled, saveLabel = 'Save', form, readOnly }: { left?: ReactNode; busy: boolean; onCancel: () => void; saveDisabled?: boolean; saveLabel?: string; form: string; readOnly?: boolean }) {
  // наблюдателю нечего сохранять и удалять: карточка остаётся, чтобы читать и спрашивать
  if (readOnly) {
    return (
      <div className="flex items-center gap-2">
        <span className="ml-auto" />
        <Button variant="ghost" onClick={onCancel}>Close</Button>
      </div>
    );
  }
  return (
    <div className="flex items-center gap-2">
      {left}
      <span className="ml-auto" />
      <Button variant="ghost" onClick={onCancel}>Cancel</Button>
      <Button type="submit" form={form} disabled={busy || saveDisabled}>{busy ? 'Saving…' : saveLabel}</Button>
    </div>
  );
}

/** Поля карточки только для чтения: fieldset с display:contents не меняет вёрстку, но гасит ввод. */
export const readOnlyFields = (on: boolean, node: ReactNode): ReactNode =>
  (on ? <fieldset disabled className="contents">{node}</fieldset> : node);
